import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Input } from 'antd';
import { ApiOutlined, BankOutlined, ExportOutlined, IdcardOutlined, SearchOutlined, StopOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';

import { Button, Chip, DataTable, PageTitle, StatCard, Tabs, Tag, type DataColumn, type TagColor } from '../components/ds';
import { adminUsersApi, type WikiUserX } from '../lib/api/admin';
import { RolesPage } from './RolesPage';
import { PctBar } from './analytics/parts';
import './analytics/analytics.css';

const CRM_USERS_URL = 'https://crm.brasol.ru/users';

const STATUS: Record<WikiUserX['status'], { label: string; color: TagColor }> = {
  active: { label: 'Активен', color: 'green' },
  invited: { label: 'Ожидает входа', color: 'gold' },
  blocked: { label: 'Заблокирован', color: 'red' },
};

/** Цвет кружка-инициалов по роли (переменные — тёмная тема). */
function colorFor(seed: string): string {
  const palette = ['var(--fk-chart-1)', 'var(--fk-chart-2)', 'var(--fk-chart-5)', 'var(--fk-chart-3)', 'var(--fk-chart-7)', 'var(--fk-chart-6)'];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function Avatar({ user }: { user: WikiUserX }): JSX.Element {
  if (user.avatarUrl) return <img src={user.avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  const initials = user.fullName.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return (
    <span style={{ width: 32, height: 32, borderRadius: '50%', background: colorFor(user.roleCode || user.id), color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
      {initials || '?'}
    </span>
  );
}

/** «Был(а)»: относительное время по lastLoginAt. */
function lastSeen(iso: string | null): string {
  if (!iso) return 'не входил';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'не входил';
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'вчера';
  if (d < 7) return `${d} дн назад`;
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * «Пользователи и роли» (/admin/users, HUB 2.0): вкладки «Пользователи»
 * (CRM-пользователи + сотрудники iiko с доступом в вики, прогресс обучения)
 * и «Роли» (доступ ролей к разделам базы). Управление пользователями и
 * ролями — в CRM (READ-ONLY здесь, «Смотреть от лица» и «Новая роль» в HUB
 * не делаем). /admin/roles ведёт сюда с ?tab=roles.
 */
export function UsersPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'roles' ? 'roles' : 'users';
  return (
    <div className="fk-adm-page">
      <PageTitle
        title="Пользователи и роли"
        sub="Аккаунты синхронизируются с CRM и iiko · управление — в CRM"
        style={{ marginBottom: 0 }}
        right={
          <Button variant="primary" icon={<UserAddOutlined />} href={CRM_USERS_URL} iconRight={<ExportOutlined />}>
            Пригласить в CRM
          </Button>
        }
      />
      <Tabs
        items={[
          { key: 'users', label: 'Пользователи' },
          { key: 'roles', label: 'Роли и доступ' },
        ]}
        value={tab}
        onChange={(k) => setSp(new URLSearchParams({ tab: k }), { replace: true })}
      />
      {tab === 'users' ? <UsersTab /> : <RolesPage embedded />}
    </div>
  );
}

function UsersTab(): JSX.Element {
  const [q, setQ] = useState('');
  const [typeF, setTypeF] = useState<'all' | 'crm' | 'employee'>('all');
  const [roleF, setRoleF] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({ queryKey: ['wiki-admin', 'users2'], queryFn: () => adminUsersApi.list({ limit: 500 }), retry: false });
  const users = useMemo(() => data ?? [], [data]);

  const roleChips = useMemo(() => {
    const by = new Map<string, number>();
    for (const u of users) if (u.roleName) by.set(u.roleName, (by.get(u.roleName) ?? 0) + 1);
    return [...by.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [users]);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (typeF !== 'all' && u.type !== typeF) return false;
      if (roleF && u.roleName !== roleF) return false;
      if (!needle) return true;
      return [u.fullName, u.email, u.phone, u.roleName, u.scope].some((x) => (x ?? '').toLowerCase().includes(needle));
    });
  }, [users, q, typeF, roleF]);

  const counts = {
    total: users.length,
    crm: users.filter((u) => u.type === 'crm').length,
    employee: users.filter((u) => u.type === 'employee').length,
    blocked: users.filter((u) => u.status === 'blocked').length,
  };

  const columns: DataColumn<WikiUserX>[] = [
    {
      key: 'name',
      title: 'Пользователь',
      value: (u) => u.fullName,
      render: (u) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar user={u} />
          <span style={{ minWidth: 0 }}>
            <b>{u.fullName}</b>
            <div className="fk-adm-note" style={{ fontFamily: 'var(--fk-font-mono)' }}>{u.email || u.phone || '—'}</div>
          </span>
        </span>
      ),
    },
    { key: 'role', title: 'Роль', value: (u) => u.roleName ?? '' , render: (u) => u.roleName ?? <span className="fk-adm-muted">—</span> },
    { key: 'scope', title: 'Точка / охват', value: (u) => u.scope ?? '', render: (u) => u.scope ?? <span className="fk-adm-muted">—</span> },
    {
      key: 'type',
      title: 'Тип',
      value: (u) => (u.type === 'crm' ? 'CRM' : 'iiko'),
      render: (u) => (u.type === 'crm' ? <Tag color="blue" icon={<BankOutlined />}>CRM</Tag> : <Tag color="indigo" icon={<ApiOutlined />}>iiko</Tag>),
    },
    { key: 'seen', title: 'Был(а)', value: (u) => u.lastLoginAt ?? '', render: (u) => <span className="fk-adm-muted">{lastSeen(u.lastLoginAt)}</span> },
    {
      key: 'training',
      title: 'Обучение',
      num: true,
      csvTitle: 'Обучение, %',
      value: (u) => u.trainingPct ?? null,
      render: (u) =>
        u.coursesStarted ? (
          <span title={`Завершено ${u.coursesCompleted ?? 0} из ${u.coursesStarted} начатых курсов`}>
            <PctBar value={u.trainingPct ?? 0} width={130} />
          </span>
        ) : (
          <span className="fk-adm-muted">не начинал</span>
        ),
    },
    { key: 'status', title: 'Статус', value: (u) => STATUS[u.status].label, render: (u) => <Tag color={STATUS[u.status].color}>{STATUS[u.status].label}</Tag> },
  ];

  if (error) return <Alert type="error" showIcon message="Не удалось загрузить пользователей" description={(error as Error).message} />;

  return (
    <>
      <div className="fk-adm-grid4">
        <StatCard icon={<TeamOutlined />} tone="orange" value={counts.total} label="всего пользователей" />
        <StatCard icon={<IdcardOutlined />} tone="blue" value={counts.crm} label="партнёры и УК (CRM)" />
        <StatCard icon={<ApiOutlined />} tone="indigo" value={counts.employee} label="сотрудники iiko" />
        <StatCard icon={<StopOutlined />} tone="red" value={counts.blocked} label="заблокированы" />
      </div>
      <div className="fk-adm-hint">
        <Input allowClear prefix={<SearchOutlined />} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по имени, точке, телефону" style={{ maxWidth: 320 }} />
        {(
          [
            ['all', 'Все'],
            ['crm', 'Партнёры и УК'],
            ['employee', 'Сотрудники iiko'],
          ] as const
        ).map(([k, l]) => (
          <Chip key={k} active={typeF === k} onClick={() => setTypeF(k)}>
            {l}
          </Chip>
        ))}
        {roleChips.length > 1 && <span style={{ width: 1, height: 20, background: 'var(--fk-border)' }} />}
        {roleChips.length > 1 &&
          roleChips.map(([r, n]) => (
            <Chip key={r} active={roleF === r} count={n} onClick={() => setRoleF(roleF === r ? null : r)}>
              {r}
            </Chip>
          ))}
      </div>
      <DataTable columns={columns} rows={list} rowKey={(u) => `${u.type}-${u.id}`} loading={isLoading} pageSize={50} emptyText="Никого не найдено" />
      <div className="fk-adm-hint">
        <BankOutlined style={{ color: 'var(--fk-orange)' }} />
        Блокировка, выдача паролей сотрудникам и смена ролей — в{' '}
        <a href={CRM_USERS_URL} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
          CRM → Пользователи
        </a>
        .
      </div>
    </>
  );
}
